import {
  AttachLoopOptions,
  ReliefOptions,
  ViewBox,
} from '@modules/Vector/S/Function/composite/svg.composite.types.ts';
import { SVG_COMPOSITE_CONSTANTS } from '@modules/Vector/S/Constant/composite.constant.ts';
import { fe, pushIf, setAttributes } from '@modules/Vector/S/Function/composite/svg.utils.ts';
import { SvgUtil } from '@modules/Vector/S/Function/util.svg.ts';
import { RELIEF_OPTIONS } from '@modules/Vector/S/Constant/doll.constant.ts';

export function buildReliefFilter(
  doc: Document,
  defs: SVGDefsElement,
  opts: ReliefOptions | undefined,
  id: string,
  vb?: ViewBox,
): string {
  const o = mergeReliefOptions(opts);

  const NS = SVG_COMPOSITE_CONSTANTS.NAMESPACES.SVG;

  const f = doc.createElementNS(NS, 'filter') as SVGFilterElement;
  setAttributes(f, {
    id,
    style: 'color-interpolation-filters:sRGB',
  });

  if (vb) {
    // blur/offset/morphology가 잘리지 않도록 안전 패딩 계산
    const outerStd = o.outer && o?.outer !== false ? (o.outer?.std ?? 0) : 0;
    const innerDarkStd = o.innerDark && o.innerDark !== false ? (o.innerDark.std ?? 0) : 0;
    const innerLightStd = o.innerLight && o.innerLight !== false ? (o.innerLight.std ?? 0) : 0;

    const maxDx = Math.max(
      Math.abs(o.outer && o.outer !== false ? (o.outer.dx ?? 0) : 0),
      Math.abs(o.innerDark && o.innerDark !== false ? (o.innerDark.dx ?? 0) : 0),
      Math.abs(o.innerLight && o.innerLight !== false ? (o.innerLight.dx ?? 0) : 0),
    );
    const maxDy = Math.max(
      Math.abs(o.outer && o.outer !== false ? (o.outer.dy ?? 0) : 0),
      Math.abs(o.innerDark && o.innerDark !== false ? (o.innerDark.dy ?? 0) : 0),
      Math.abs(o.innerLight && o.innerLight !== false ? (o.innerLight.dy ?? 0) : 0),
    );

    // 경험값: std*3(가우시안 영향 반경) + spread(morphology) + offset(dx/dy) + 여유 2px
    const pad = Math.ceil(
      (outerStd + innerDarkStd + innerLightStd) * 3 + (o.spread ?? 0) + (maxDx + maxDy) + 2,
    );

    setAttributes(f, {
      filterUnits: 'userSpaceOnUse',
      primitiveUnits: 'userSpaceOnUse',
      x: String(vb.x - pad),
      y: String(vb.y - pad),
      width: String(vb.width + pad * 2),
      height: String(vb.height + pad * 2),
    });
  }

  // (a) Outer shadow (optional)
  if (o.outer !== false) {
    const { dx, dy, std, color, opacity } = o.outer;
    const drop = doc.createElementNS(NS, 'feDropShadow') as SVGFEDropShadowElement;
    setAttributes(drop, {
      dx: String(dx),
      dy: String(dy),
      stdDeviation: String(std),
      ...(color != null ? { 'flood-color': color } : {}),
      ...(opacity != null ? { 'flood-opacity': String(opacity) } : {}),
      result: 'outer',
    });
    f.appendChild(drop);
  }

  // (b) Spread / erode (alpha 확장/축소)
  const morph = doc.createElementNS(NS, 'feMorphology') as SVGFEMorphologyElement;
  setAttributes(morph, {
    in: 'SourceAlpha',
    operator: 'erode',
    radius: String(o.spread),
    result: 'alphaSpread',
  });
  f.appendChild(morph);

  // (c) Inner dark
  if (o.innerDark !== false) {
    const { dx, dy, std, color, opacity } = o.innerDark;
    const b = fe(doc, 'feGaussianBlur', {
      in: 'alphaSpread',
      ...(std != null ? { stdDeviation: std } : {}),
      result: 'ib1',
    });
    const off = fe(doc, 'feOffset', {
      in: 'ib1',
      ...(dx != null ? { dx } : {}),
      ...(dy != null ? { dy } : {}),
      result: 'io1',
    });
    const ar = fe(doc, 'feComposite', {
      in: 'io1',
      in2: 'SourceAlpha',
      operator: 'arithmetic',
      k2: -1,
      k3: 1,
      result: 'innerDark',
    });
    const flood = fe(doc, 'feFlood', {
      ...(color != null ? { 'flood-color': color } : {}),
      ...(opacity != null ? { 'flood-opacity': opacity } : {}),
      result: 'dc',
    });
    const paint = fe(doc, 'feComposite', {
      in: 'dc',
      in2: 'innerDark',
      operator: 'in',
      result: 'innerDarkFill',
    });
    [b, off, ar, flood, paint].forEach((n) => f.appendChild(n));
  }

  // (d) Inner light
  if (o.innerLight !== false) {
    const { dx, dy, std, color, opacity } = o.innerLight;
    const b = fe(doc, 'feGaussianBlur', {
      in: 'alphaSpread',
      ...(std != null ? { stdDeviation: std } : {}),
      result: 'ib2',
    });
    const off = fe(doc, 'feOffset', {
      in: 'ib2',
      ...(dx != null ? { dx } : {}),
      ...(dy != null ? { dy } : {}),
      result: 'io2',
    });
    const ar = fe(doc, 'feComposite', {
      in: 'io2',
      in2: 'SourceAlpha',
      operator: 'arithmetic',
      k2: -1,
      k3: 1,
      result: 'innerLight',
    });
    const flood = fe(doc, 'feFlood', {
      ...(color != null ? { 'flood-color': color } : {}),
      ...(opacity != null ? { 'flood-opacity': opacity } : {}),
      result: 'lc',
    });
    const paint = fe(doc, 'feComposite', {
      in: 'lc',
      in2: 'innerLight',
      operator: 'in',
      result: 'innerLightFill',
    });
    [b, off, ar, flood, paint].forEach((n) => f.appendChild(n));
  }

  // (e) Specular (optional)
  if (o.specular) {
    const { surfaceScale, exponent, constant, light } = o.specular;
    const blur = fe(doc, 'feGaussianBlur', { in: 'SourceAlpha', stdDeviation: 1.2, result: 'surf' });
    const spec = doc.createElementNS(NS, 'feSpecularLighting');
    setAttributes(spec, {
      in: 'surf',
      surfaceScale: String(surfaceScale),
      specularConstant: String(constant),
      specularExponent: String(exponent),
      'lighting-color': '#ffffff',
      result: 'spec',
    });

    if (light) {
      const l = doc.createElementNS(NS, 'fePointLight');
      setAttributes(l, {
        x: String(light.x),
        y: String(light.y),
        z: String(light.z),
      });
      spec.appendChild(l);
    }

    const keep = fe(doc, 'feComposite', { in: 'spec', in2: 'SourceAlpha', operator: 'in', result: 'specIn' });
    [blur, spec, keep].forEach((n) => f.appendChild(n));
  }

  // (f) merge effects (inset 계열 합치기)
  {
    const merge = doc.createElementNS(NS, 'feMerge');
    pushIf(merge, o.innerDark !== false ? fe(doc, 'feMergeNode', { in: 'innerDarkFill' }) : null);
    pushIf(merge, o.innerLight !== false ? fe(doc, 'feMergeNode', { in: 'innerLightFill' }) : null);
    pushIf(merge, o.specular ? fe(doc, 'feMergeNode', { in: 'specIn' }) : null);
    setAttributes(merge, { result: 'insetAll' });
    f.appendChild(merge);
  }

  {
    const insetFeather = fe(doc, 'feGaussianBlur', {
      in: 'insetAll',
      stdDeviation: 8,
      result: 'insetAllFeather',
    });
    f.appendChild(insetFeather);
  }

  // (g) compose with SourceGraphic
  {
    // screen or over
    const node =
      o.compositeMode === 'screen'
        ? (() => {
            const blend = doc.createElementNS(NS, 'feBlend');
            setAttributes(blend, {
              in: 'SourceGraphic',
              in2: 'insetAllFeather',
              mode: 'screen',
              result: 'withBevel',
            });
            return blend;
          })()
        : (() => {
            const over = doc.createElementNS(NS, 'feComposite');
            setAttributes(over, {
              in: 'insetAllFeather',
              in2: 'SourceGraphic',
              operator: 'over',
              result: 'withBevel',
            });
            return over;
          })();

    f.appendChild(node);
  }

  // (h) outer와 합치기
  if (o.outer !== false) {
    const merge2 = doc.createElementNS(NS, 'feMerge');
    merge2.appendChild(fe(doc, 'feMergeNode', { in: 'outer' }));
    merge2.appendChild(fe(doc, 'feMergeNode', { in: 'withBevel' }));
    f.appendChild(merge2);
  } else {
    // outer가 없으면 withBevel만 그대로 출력되도록 결과 유지
    // (결과명 소비는 외부 <filter> 적용 대상이 하므로 여기선 추가 작업 불필요)
  }

  defs.appendChild(f);
  return id;
}

export function mergeReliefOptions(opts?: ReliefOptions) {
  const o: ReliefOptions = { ...RELIEF_OPTIONS.DEFAULT, ...(opts ?? {}) };
  // outer/innerDark/innerLight/specular이 false인 경우 유지
  // true/undefined면 기본값 채우기
  if (o.outer === undefined) o.outer = RELIEF_OPTIONS.DEFAULT.outer;
  if (o.innerDark === undefined) o.innerDark = RELIEF_OPTIONS.DEFAULT.innerDark;
  if (o.innerLight === undefined) o.innerLight = RELIEF_OPTIONS.DEFAULT.innerLight;
  if (o.specular === undefined) o.specular = RELIEF_OPTIONS.DEFAULT.specular;
  if (o.spread === undefined) o.spread = RELIEF_OPTIONS.DEFAULT.spread;
  if (!o.compositeMode) o.compositeMode = RELIEF_OPTIONS.DEFAULT.compositeMode;
  if (!o.idPrefix) o.idPrefix = '';
  return o as Required<ReliefOptions>;
}
/**
 * svg 내부에 <image>를 붙임.
 * @param svg      루트 <svg>
 * @param target   최상단 점을 구할 대상 도형
 * @param pngHref  PNG 경로
 * @param w/h      SVG 사용자단위 기준 크기
 * @param offsetY  위로 살짝 빼줄 양(양수면 위로 이동)
 * @param anchor   'center'면 x 중앙정렬, 'left'면 왼쪽 맞춤
 */

export const attachLoop = async ({
  svg,
  target,
  pngHref,
  width,
  height,
  offsetY = 0,
  anchor = 'center',
}: AttachLoopOptions) => {
  const top = await SvgUtil.findTopPointInSvgPath({ svgString: target });

  if (!top) return;

  const img = document.createElementNS(SVG_COMPOSITE_CONSTANTS.NAMESPACES.SVG, 'image');
  const dataUri = await SvgUtil.urlToDataUriCached(pngHref);

  img.setAttributeNS(SVG_COMPOSITE_CONSTANTS.NAMESPACES.XLINK, 'href', dataUri);

  const x = anchor === 'center' ? top.x - width / 2 : top.x;
  const y = top.y - height - offsetY;

  img.setAttribute('x', String(x));
  img.setAttribute('y', String(y));
  img.setAttribute('width', String(width));
  img.setAttribute('height', String(height));
  img.setAttribute('data-role', 'ring');

  svg.appendChild(img);
};

export const attachMaterial = async ({ svg }: { svg: SVGSVGElement }) => {
  const vb = svg.viewBox.baseVal;
  const vx = vb?.x ?? 0;
  const vy = vb?.y ?? 0;
  const vw = vb?.width || svg.width.baseVal.value || 0;
  const vh = vb?.height || svg.height.baseVal.value || 0;

  const clipPath = svg.querySelector(SVG_COMPOSITE_CONSTANTS.SELECTORS.MATERIAL_GROUP) as SVGUseElement;

  const img = document.createElementNS(SVG_COMPOSITE_CONSTANTS.NAMESPACES.SVG, 'image');
  const dataUri = await SvgUtil.urlToDataUriCached(
    '//s3.marpple.co/files/u_2798351/2025/10/original/430c8899c01fd1e705a2c88a13adeebc45dd76dd1.png',
  );

  img.setAttributeNS(SVG_COMPOSITE_CONSTANTS.NAMESPACES.XLINK, 'href', dataUri);

  setAttributes(img, {
    x: String(vx),
    y: String(vy),
    width: String(vw),
    height: String(vh),
    preserveAspectRatio: 'xMidYMid slice',
    style: 'pointer-events:none',
  });

  clipPath.appendChild(img);
};
